import * as THREE from 'three';
import { EnvironmentSystemBase } from '../EnvironmentSystemBase';
import { CloudConfig, CloudRenderMode, CloudType } from '../types';

const DEFAULTS: CloudConfig = {
    density: 0.5,
    speed: 0.1,
    height: 25,
    type: CloudType.CUMULUS,
    mode: CloudRenderMode.VOLUMETRIC,
    hybridOpacity: 0.3,
    textureUrl: undefined,
    spriteSize: 2
};

export class CloudSystem extends EnvironmentSystemBase {
    public config: CloudConfig;
    public mode: CloudRenderMode;

    private particleSystems: THREE.Points[] = [];
    private particleMaterials: THREE.PointsMaterial[] = [];
    private spriteMeshes: THREE.Mesh[] = [];
    private textureLoader: THREE.TextureLoader = new THREE.TextureLoader();

    constructor(name: string, cfg?: Partial<CloudConfig>) {
        super(name);
        this.config = { ...DEFAULTS, ...(cfg || {}) };
        this.mode = this.config.mode;
    }

    setConfig(cfg: Partial<CloudConfig>): void {
        this.config = { ...this.config, ...cfg };
        this.mode = this.config.mode;
        this.rebuild();
    }

    setMode(mode: CloudRenderMode): void {
        this.mode = mode;
        this.config.mode = mode;
        this.rebuild();
    }

    setType(type: CloudType): void {
        this.config.type = type;
        this.rebuild();
    }

    protected build(): void {
        this.group.clear();
        this.particleSystems = [];
        this.particleMaterials = [];
        this.spriteMeshes = [];
        // Minimal placeholder implementation: particle clouds
        if (this.mode === CloudRenderMode.PARTICLES || this.mode === CloudRenderMode.HYBRID) {
            // If multiple textures provided -> use instanced quads with correct aspect
            if (this.config.textureUrls && this.config.textureUrls.length > 0) {
                this.createSpriteBatchesInstanced();
            } else {
                const systems = this.createParticleCloudsMulti();
                systems.forEach(ps => this.group.add(ps));
            }
        }
        // Volumetric placeholders can be groups of spheres for now
        if (this.mode === CloudRenderMode.VOLUMETRIC || this.mode === CloudRenderMode.HYBRID) {
            const volumetric = this.createVolumetricPlaceholders();
            this.group.add(volumetric);
        }
    }

    private rebuild(): void {
        this.build();
        this.updateAppearance();
        this.updateHeight();
    }

    protected onUpdate(dt: number): void {
        if (!this.enabled) return;
        // Drift for point clouds
        for (const ps of this.particleSystems) {
            const pos = ps.geometry.getAttribute('position') as THREE.BufferAttribute;
            const array = pos.array as Float32Array;
            for (let i = 0; i < array.length; i += 3) {
                array[i] += Math.sin((i + performance.now() * 0.001) * 0.0003) * this.config.speed * 0.05;
                array[i + 2] += Math.cos((i + performance.now() * 0.001) * 0.0002) * this.config.speed * 0.03;
            }
            pos.needsUpdate = true;
        }

        // Sprite batches: GPU billboard via shader, only update instance offsets for gentle drift
        if (this.spriteMeshes.length) {
            for (let b = 0; b < this.spriteMeshes.length; b++) {
                const mesh = this.spriteMeshes[b];
                const geo = mesh.geometry as THREE.InstancedBufferGeometry;
                const offsetAttr = geo.getAttribute('iOffset') as THREE.InstancedBufferAttribute;
                if (offsetAttr) {
                    const arr = offsetAttr.array as Float32Array;
                    for (let i = 0; i < arr.length; i += 3) {
                        arr[i] += Math.sin((i + performance.now() * 0.001) * 0.0003) * this.config.speed * 0.02;
                        arr[i + 2] += Math.cos((i + performance.now() * 0.001) * 0.0002) * this.config.speed * 0.02;
                    }
                    offsetAttr.needsUpdate = true;
                }
            }
        }
    }

    private createParticleCloudsMulti(): THREE.Points[] {
        const particleCount = Math.max(200, Math.floor(1800 * this.config.density));
        const textureUrls: (string | undefined)[] = this.config.textureUrls && this.config.textureUrls.length > 0
            ? this.config.textureUrls
            : [this.config.textureUrl];

        const systems: THREE.Points[] = [];
        const batches = Math.max(1, textureUrls.filter(Boolean).length);
        const perBatch = Math.ceil(particleCount / batches);
        const sizeMin = this.config.spriteSizeRange?.[0] ?? (this.config.spriteSize ?? 2);
        const sizeMax = this.config.spriteSizeRange?.[1] ?? (this.config.spriteSize ?? 2);

        for (let b = 0; b < batches; b++) {
            const count = b === batches - 1 ? (particleCount - perBatch * (batches - 1)) : perBatch;
            const geometry = new THREE.BufferGeometry();
            const positions = new Float32Array(count * 3);
            const colors = new Float32Array(count * 3);
            const sizes = new Float32Array(count);
            const spreadX = this.config.spreadX ?? 80;
            const spreadZ = this.config.spreadZ ?? 80;
            for (let i = 0; i < count; i++) {
                const i3 = i * 3;
                positions[i3] = (Math.random() - 0.5) * spreadX;
                positions[i3 + 1] = this.config.height + (Math.random() - 0.5) * 8;
                positions[i3 + 2] = (Math.random() - 0.5) * spreadZ;
                const br = 0.8 + Math.random() * 0.2;
                colors[i3] = br; colors[i3 + 1] = br; colors[i3 + 2] = br;
                sizes[i] = sizeMin + Math.random() * (sizeMax - sizeMin);
            }
            geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
            geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
            geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

            const material = new THREE.PointsMaterial({
                size: this.config.spriteSize ?? 2,
                sizeAttenuation: true,
                vertexColors: true,
                transparent: true,
                opacity: this.mode === CloudRenderMode.HYBRID ? (this.config.hybridOpacity ?? 0.3) : 0.6,
                blending: THREE.AdditiveBlending,
                depthWrite: false
            });

            const url = textureUrls[b] as string | undefined;
            if (url) {
                this.textureLoader.load(url, (tex) => {
                    tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
                    tex.minFilter = THREE.LinearFilter;
                    tex.magFilter = THREE.LinearFilter;
                    material.map = tex;
                    material.alphaTest = 0.01;
                    material.needsUpdate = true;
                });
            }

            const points = new THREE.Points(geometry, material);
            systems.push(points);
            this.particleSystems.push(points);
            this.particleMaterials.push(material);
        }

        return systems;
    }

    // Instanced quads per texture with correct aspect and size from pixel dimensions
    private createSpriteBatchesInstanced(): void {
        const textureUrls = this.config.textureUrls || [];
        const batches = textureUrls.length;
        const particleCount = Math.max(400, Math.floor(2400 * this.config.density));
        const perBatch = Math.ceil(particleCount / Math.max(1, batches));
        const ppu = this.config.spritePixelsPerUnit ?? 64; // default: 64 pixels = 1 unit
        const sizeMin = this.config.spriteSizeRange?.[0] ?? 1;
        const sizeMax = this.config.spriteSizeRange?.[1] ?? 1;

        for (let b = 0; b < batches; b++) {
            const url = textureUrls[b];
            this.textureLoader.load(url, (tex) => {
                const img: any = tex.image;
                const imgW = img?.width ?? 128;
                const imgH = img?.height ?? 128;
                const baseW = imgW / ppu;
                const baseH = imgH / ppu;

                // Build instanced geometry with per-instance offset and scale attributes
                const base = new THREE.PlaneGeometry(1, 1);
                const geo = new THREE.InstancedBufferGeometry();
                geo.index = base.index;
                geo.setAttribute('position', base.getAttribute('position'));
                geo.setAttribute('uv', base.getAttribute('uv'));

                const count = b === batches - 1 ? (particleCount - perBatch * (batches - 1)) : perBatch;
                const offsets = new Float32Array(count * 3);
                const scales = new Float32Array(count * 2);
                const spreadX = this.config.spreadX ?? 80;
                const spreadZ = this.config.spreadZ ?? 80;
                for (let i = 0; i < count; i++) {
                    const i3 = i * 3;
                    const i2 = i * 2;
                    offsets[i3] = (Math.random() - 0.5) * spreadX;
                    offsets[i3 + 1] = this.config.height + (Math.random() - 0.5) * 8;
                    offsets[i3 + 2] = (Math.random() - 0.5) * spreadZ;
                    const rnd = sizeMin + Math.random() * (sizeMax - sizeMin);
                    scales[i2] = baseW * rnd;
                    scales[i2 + 1] = baseH * rnd;
                }
                geo.setAttribute('iOffset', new THREE.InstancedBufferAttribute(offsets, 3));
                geo.setAttribute('iScale', new THREE.InstancedBufferAttribute(scales, 2));

                const mat = new THREE.MeshBasicMaterial({
                    map: tex,
                    transparent: true,
                    depthWrite: false,
                    alphaTest: 0.01,
                    blending: THREE.NormalBlending,
                    side: THREE.DoubleSide
                });

                // GPU billboarding in vertex shader (respect parent scale)
                mat.onBeforeCompile = (shader: any) => {
                    shader.uniforms.uYawOnly = { value: false };
                    shader.vertexShader = shader.vertexShader
                        .replace('#include <common>', `#include <common>\nattribute vec3 iOffset;\nattribute vec2 iScale;\nuniform bool uYawOnly;`)
                        .replace('#include <project_vertex>', `\n// view-space billboard with model scale\n// estimate uniform scale from modelMatrix columns\nfloat sx = length(modelMatrix[0].xyz);\nfloat sy = length(modelMatrix[1].xyz);\nfloat sz = length(modelMatrix[2].xyz);\nfloat uScale = (sx + sy + sz) / 3.0;\nvec2 finalScale = iScale * uScale;\nvec4 mvPosition = modelViewMatrix * vec4( iOffset, 1.0 );\nvec2 offset = position.xy * finalScale;\n// yaw-only option can be implemented later if needed\nmvPosition.xy += offset;\ngl_Position = projectionMatrix * mvPosition;`);
                };

                const mesh = new THREE.Mesh(geo, mat);
                mesh.frustumCulled = false;
                this.group.add(mesh);
                this.spriteMeshes.push(mesh);
            });
        }
    }

    private createVolumetricPlaceholders(): THREE.Group {
        const group = new THREE.Group();
        const mainCount = 3;
        for (let c = 0; c < mainCount; c++) {
            const formation = new THREE.Group();
            formation.position.set((Math.random() - 0.5) * 60, this.config.height, (Math.random() - 0.5) * 60);
            const density = 8 + Math.floor(Math.random() * 8);
            for (let i = 0; i < density; i++) {
                const puffSize = 3 + Math.random() * 5;
                const geo = new THREE.SphereGeometry(puffSize, 12, 10);
                const mat = new THREE.MeshLambertMaterial({ color: 0xffffff, transparent: true, opacity: 0.7 });
                const puff = new THREE.Mesh(geo, mat);
                puff.position.set((Math.random() - 0.5) * 10, (Math.random() - 0.5) * 3, (Math.random() - 0.5) * 10);
                formation.add(puff);
            }
            group.add(formation);
        }
        return group;
    }

    private updateAppearance(): void {
        // Adjust opacity/density basics
        this.group.traverse(obj => {
            const mesh = obj as THREE.Mesh;
            if ((mesh as any).material && (mesh as any).material.transparent) {
                (mesh as any).material.opacity = 0.5 + this.config.density * 0.5;
            }
        });
        if (this.particleMaterials.length) {
            for (const mat of this.particleMaterials) {
                mat.size = this.config.spriteSize ?? 2;
                mat.opacity = this.mode === CloudRenderMode.HYBRID ? (this.config.hybridOpacity ?? 0.3) : 0.6;
                mat.needsUpdate = true;
            }
        }
    }

    private updateHeight(): void {
        this.group.traverse(obj => { obj.position.y = this.config.height; });
    }
}
